import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import ReviewDetail from "@/modules/review/components/review-detail";
import { DeleteConfirmationModal } from "@/modules/review/components/delete-confirmation-modal";
import Container from "@/components/ui/container";
import Button from "@/components/ui/button";
import { useReviews } from "@/modules/review/hooks/use-reviews";
import { useAuth } from "@/modules/auth";
import type { ReviewResponse, ReviewTag } from "@/modules/review/types";

type ReviewWithTags = ReviewResponse & { tags?: ReviewTag[]; audio_url?: string };

export default function ViewReviewPage() {
	const navigate = useNavigate();
	const { brandUrl, reviewId } = useParams();
	const { currentWorkspaceId } = useAuth();
	const { showReview, deleteReview } = useReviews();

	const [review, setReview] = useState<ReviewWithTags | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	useEffect(() => {
		const fetchReview = async () => {
			if (!currentWorkspaceId || !reviewId) return;

			setIsLoading(true);
			setError(null);

			try {
				const result = await showReview(currentWorkspaceId, reviewId);
				if (result) {
					setReview(result as ReviewWithTags);
				} else {
					setError("Review not found");
				}
			} catch (err) {
				setError("Failed to load review");
			} finally {
				setIsLoading(false);
			}
		};

		fetchReview();
	}, [currentWorkspaceId, reviewId, showReview]);

	const handleBack = () => {
		navigate(`/${brandUrl}/reviews`);
	};

	const handleEdit = () => {
		navigate(`/${brandUrl}/reviews/${reviewId}/edit`);
	};

	const handleDeleteConfirm = async () => {
		if (!currentWorkspaceId || !reviewId) return;

		setIsDeleting(true);
		try {
			const result = await deleteReview(currentWorkspaceId, reviewId);
			if (result) {
				setDeleteModalOpen(false);
				navigate(`/${brandUrl}/reviews`);
			}
		} catch (err) {
			console.error("Failed to delete review:", err);
		} finally {
			setIsDeleting(false);
		}
	};

	// Loading state
	if (isLoading) {
		return (
			<Container className="py-12">
				<div className="flex items-center justify-center min-h-[400px]">
					<div className="flex flex-col items-center gap-3">
						<svg className="w-8 h-8 text-brand animate-spin" fill="none" viewBox="0 0 24 24">
							<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
							<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
						</svg>
						<span className="text-tertiary">Loading review...</span>
					</div>
				</div>
			</Container>
		);
	}

	// Error state
	if (error) {
		return (
			<Container className="py-12">
				<div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
					<div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-2">
						<svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={1.5}
								d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
							/>
						</svg>
					</div>
					<h2 className="text-lg font-semibold text-primary">Error loading review</h2>
					<p className="text-tertiary">{error}</p>
					<button onClick={handleBack} className="text-brand hover:underline mt-2">
						Back to reviews
					</button>
				</div>
			</Container>
		);
	}

	// Not found state
	if (!review) {
		return (
			<Container className="py-12">
				<div className="flex flex-col items-center justify-center min-h-100 gap-4">
					<div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-2">
						<svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
						</svg>
					</div>
					<h2 className="text-lg font-semibold text-primary">Review not found</h2>
					<p className="text-tertiary">This review may have been deleted or does not exist.</p>
					<button onClick={handleBack} className="text-brand hover:underline mt-2">
						Back to reviews
					</button>
				</div>
			</Container>
		);
	}

	// Success state
	return (
		<>
			<Container className="py-12">
				{/* Page Header */}
				<div className="mb-8 flex items-center justify-between">
					<div className="flex items-center gap-4">
						<button onClick={handleBack} className="flex items-center gap-2 text-tertiary hover:text-primary transition-colors">
							<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
							</svg>
							<span>Back</span>
						</button>
						<div className="w-px h-6 bg-gray-200" />
						<h1 className="text-xl font-semibold text-primary">View Review</h1>
					</div>
				</div>

				{/* Review Detail Component */}
				<ReviewDetail review={review} />
			</Container>

			{/* Sticky footer with action buttons */}
			<div className="fixed bottom-0 left-0 z-20 bg-white border-t border-gray-200 px-8 py-4 w-[calc(100%-256px)] ml-64">
				<Container>
					<div className="flex items-center justify-between">
						<button onClick={handleBack} className="flex items-center gap-2 text-tertiary hover:text-primary transition-colors">
							<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
							</svg>
							<span>Back to Reviews</span>
						</button>
						<div className="flex items-center gap-3">
							<Button type="button" variant="light" onClick={() => setDeleteModalOpen(true)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
								Delete
							</Button>
							<Button type="button" variant="default" onClick={handleEdit}>
								Edit Review
							</Button>
						</div>
					</div>
				</Container>
			</div>

			{/* Delete Confirmation Modal */}
			<DeleteConfirmationModal
				open={deleteModalOpen}
				onOpenChange={setDeleteModalOpen}
				onConfirm={handleDeleteConfirm}
				isLoading={isDeleting}
				title="Delete Review"
				description="Are you sure you want to delete this review? This action cannot be undone."
				confirmText="Delete Review"
			/>
		</>
	);
}
